import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuditorDashboard } from '../hook/useAuditorDashboard.mjs';
import { getReportStatus, buildReportStatusFilterOptions }
  from '../../utils/statusConfig.mjs';
import MassReviewOverlay from '../components/MassReviewOverlay.mjs';
import ContractorClaimHistory from '../components/ContractorClaimHistory.mjs';
import monthlyReportService from '../service/monthlyReportService.mjs';
import {
  seedFiscalYear, fiscalYearRange, fiscalMonthRange, fiscalYearLabel,
  fiscalYearShortLabel, fiscalYearOptions, FISCAL_MONTHS,
} from '../../utils/fiscalYear.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import PageShell from '../../components/PageShell.mjs';
import ClarificationDrawer from '../../clarifications/components/ClarificationDrawer.mjs';
import QuestionBadge from '../../clarifications/components/QuestionBadge.mjs';
import { useOpenQuestions } from '../../clarifications/hook/useOpenQuestions.mjs';
import { withOpenQuestionsConfirm, isCancelled } from '../../clarifications/util/confirmOpenQuestions.mjs';
import { downloadAuditOutcomesExcel } from '../../reports/util/auditOutcomesExcel.mjs';


const AuditorDashboard = () => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { alert, confirm } = useDialog();
  // Clarification questions on the queued claims, for the row badges, and
  // the claim whose questions are open in the side drawer.
  const { forClaim: questionsForClaim } = useOpenQuestions();
  const [questionClaim, setQuestionClaim] = useState(null);
  const navigate = useNavigate();
  const {
    dashboardData,
    pendingAuditClaims,
    contractorClaims,
    contractorLoading,
    contractorHistory,
    contractorHistoryLoading,
    loading,
    pendingAuditLoading,
    error,
    forbidden,
    pendingAuditForbidden,
    auditingClaimId,
    filters,
    regions,
    years,
    months,
    updateFilter,
    refreshData,
    clearFilters,
    auditClaim,
    returnClaim,
  } = useAuditorDashboard();

  // The tab lives in the URL rather than component state, so returning to this
  // page - via browser Back out of a claim, or the report page's Back to
  // Dashboard - lands on the tab you actually left from instead of always
  // resetting to Monthly Summary Reports.
  const [searchParams, setSearchParams] = useSearchParams();
  // Three tabs now, so the binary read this used ('claims' or else reports)
  // would have silently swallowed the new one.
  const TABS = ['reports', 'claims', 'contractors'];
  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.includes(requestedTab) ? requestedTab : 'reports';
  const setActiveTab = (tab) =>
    setSearchParams(tab === 'reports' ? {} : { tab }, { replace: true });
  // Per-claim comment state for the inline audit form: { [claimId]: string }
  const [auditComments, setAuditComments] = useState({});
  // Which claim has the comment box open
  const [expandedAuditId, setExpandedAuditId] = useState(null);
  // Guided claim-by-claim walkthrough over the pending-audit list
  const [showMassAudit, setShowMassAudit] = useState(false);
  // Contractor tab: all | waiting | approved | sentBack.
  const [contractorView, setContractorView] = useState('all');
  const [rejectionReportBusy, setRejectionReportBusy] = useState(false);
  // The rejection report's period is chosen independently of the report-list
  // filters above it: those pick which monthly reports to look at, this picks
  // which window of AUDIT DECISIONS to count, and the two rarely coincide.
  const [rrBasis, setRrBasis] = useState('fiscal');       // 'fiscal' | 'calendar'
  const [rrFiscalYear, setRrFiscalYear] = useState(() => seedFiscalYear());
  const [rrCalendarYear, setRrCalendarYear] = useState(() => new Date().getFullYear());
  const [rrMonth, setRrMonth] = useState('all');          // 'all' | 1..12

  /**
   * Rejected vs approved claims report, for tracking return rates.
   *
   * Honours the dashboard's region/year/month filters so the report matches what
   * is on screen. Three sheets: a summary, the per-region rates, and every
   * individual decision with its reason - the reasons are the part that makes a
   * rate actionable rather than just a number.
   */
  /**
   * The window of audit decisions the report covers.
   *
   * Seed's fiscal year runs 1 Sep - 31 Aug, so a calendar year splits it in two
   * and a report run on a calendar basis would straddle two budgets. Fiscal is
   * therefore the default. Boundaries come from utils/fiscalYear.mjs, which
   * builds them from local components - a toISOString() on a local midnight
   * would move the edge a day for anyone ahead of UTC.
   */
  const rejectionReportPeriod = () => {
    const month = rrMonth === 'all' ? null : parseInt(rrMonth, 10);

    if (rrBasis === 'fiscal') {
      if (month) {
        const { from, to } = fiscalMonthRange(rrFiscalYear, month);
        const name = FISCAL_MONTHS.find(m => m.value === month)?.label || month;
        return { from, to, periodLabel: `${name} ${from.getFullYear()} · ${fiscalYearShortLabel(rrFiscalYear)}` };
      }
      const { from, to } = fiscalYearRange(rrFiscalYear);
      return { from, to, periodLabel: fiscalYearLabel(rrFiscalYear) };
    }

    const year = rrCalendarYear;
    if (month) {
      return {
        from: new Date(year, month - 1, 1, 0, 0, 0, 0),
        to: new Date(year, month, 0, 23, 59, 59, 999),
        periodLabel: `${FISCAL_MONTHS.find(m => m.value === month)?.label || month} ${year}`,
      };
    }
    return {
      from: new Date(year, 0, 1, 0, 0, 0, 0),
      to: new Date(year, 11, 31, 23, 59, 59, 999),
      periodLabel: `Calendar ${year}`,
    };
  };

  const downloadRejectionReport = async () => {
    setRejectionReportBusy(true);
    try {
      const { from, to, periodLabel } = rejectionReportPeriod();

      const data = await monthlyReportService.getAuditOutcomes({
        from: from.toISOString(), to: to.toISOString(), region: filters.region,
      });

      if (!data || data.totals.decisions === 0) {
        alert('No audit decisions were recorded in the selected period, so there is nothing to report.');
        return;
      }

      // The workbook is built by the shared util so this download and the
      // Reports page's Rejections tab stay identical.
      await downloadAuditOutcomesExcel(data, { region: filters.region, periodLabel, from, to });
    } catch (err) {
      alert(`Could not generate the rejection report: ${err.message}`);
    } finally {
      setRejectionReportBusy(false);
    }
  };

  // Open a claim read-only, matching how the monthly report's View works.
  const handleViewClaim = (claimId, claimType) => {
    const normalized = String(claimType || '').toLowerCase();
    const basePath = normalized === 'perdiemclaim' || normalized === 'perdiem'
      ? '/perdiem/claims'
      : '/expense/claims';
    navigate(`/admin${basePath}/${claimId}`, {
      state: { from: `/auditor-dashboard?tab=${activeTab}` },
    });
  };

  const handleViewReport = (reportId) => {
    navigate(`/monthly-report/${reportId}`, {
      state: { from: `/auditor-dashboard?tab=${activeTab}` },
    });
  };

  const getTotalAmount = () => {
    if (!Array.isArray(dashboardData)) return 0;
    // Coerce: totalAmount can arrive as a DECIMAL string, and `0 + "1.2"` concatenates
    // into a bad string → NaN in formatCurrency.
    return dashboardData.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
  };

  const getClaimsCount = (item) => {
    return item.claimsCount || (item.claims ? item.claims.length : 0);
  };


  const formatPeriod = (month, year) => {
    const monthName = new Date(0, month - 1).toLocaleString('default', { month: 'long' });
    return `${monthName} ${year}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getMonthName = (monthNumber) => {
    return new Date(0, monthNumber - 1).toLocaleString('default', { month: 'long' });
  };

  const handleFilterChange = (filterType, value) => {
    if (updateFilter) updateFilter(filterType, value);
  };

  const handleAuditClaim = async (claimId, claimType) => {
    const comments = auditComments[claimId] || '';
    try {
      // An open question on the claim warns first; carrying on resends with
      // the acknowledgement.
      await withOpenQuestionsConfirm(confirm, (acknowledgeOpenQuestions) =>
        auditClaim(claimId, claimType, comments, { acknowledgeOpenQuestions }));
    } catch (err) {
      if (isCancelled(err)) return;
      throw err;
    }
    setExpandedAuditId(null);
    setAuditComments(prev => { const n = { ...prev }; delete n[claimId]; return n; });
  };

  // Return a pending claim for revision - requires a comment. Sets the claim to
  // Under Review for the admin (who is emailed the reason).
  const handleReturnClaim = async (claimId, claimType) => {
    const comments = (auditComments[claimId] || '').trim();
    if (!comments) {
      alert('Please add a comment explaining what needs revision before asking for a resubmit.');
      return;
    }
    try {
      await returnClaim(claimId, claimType, comments);
      setExpandedAuditId(null);
      setAuditComments(prev => { const n = { ...prev }; delete n[claimId]; return n; });
    } catch (err) {
      alert('Failed to return claim: ' + err.message);
    }
  };

  /**
   * Which queue the claims tab is showing, and what to call it.
   *
   * Pre-approval and contractor approval are the SAME screen doing the same
   * thing to a different set of claims - the only real difference is what the
   * auditor's decision means afterwards. Rendering one body for both keeps them
   * from drifting; a second copy of this table would be 240 lines that have to
   * be fixed twice every time.
   */
  const isContractorTab = activeTab === 'contractors';
  const auditQueue = isContractorTab ? contractorClaims : pendingAuditClaims;
  // The Contractor tab always shows every contractor claim the auditor has to
  // do with - waiting, approved, sent back (the user, 2026-10-21) - and can be
  // narrowed to one of them.
  const showQueue = !isContractorTab || contractorView === 'all' || contractorView === 'waiting';
  const queueCopy = isContractorTab
    ? {
        // Coaches end at the auditor too, as contractors do (2026-10-21).
        heading: 'Contractors & Coaches',
        blurb: 'Independent contractors\u2019 and coaches\u2019 claims: those waiting on you, those you approved, '
          + 'and those sent back to their admin. You are the final approver: there is no director stage after '
          + 'this, so approving here completes the claim.',
        massTitle: 'Contractors & Coaches',
        massSubtitle: 'Contractor and coach claims awaiting final approval',
        emptyTitle: 'No contractor or coach claims waiting',
        emptyBody: 'They appear here once their admin has approved them.',
      }
    : {
        heading: 'Claims Pre-Approval',
        blurb: 'These claims have been approved by their admin but not yet individually audited. '
          + 'You may audit them here ahead of time, or audit them when reviewing the monthly report.',
        massTitle: 'Mass Audit',
        massSubtitle: 'Claims awaiting pre-approval',
        emptyTitle: 'All Caught Up',
        emptyBody: 'No claims are waiting to be audited.',
      };

  // Group pending claims by region for display
  const pendingByRegion = auditQueue.reduce((acc, claim) => {
    const region = claim.region || claim.userRegion || 'Unknown Region';
    if (!acc[region]) acc[region] = [];
    acc[region].push(claim);
    return acc;
  }, {});

  const pendingRegions = Object.keys(pendingByRegion).sort();

  // Flattened in the same region order the table shows, so the walkthrough
  // advances in the order the auditor is looking at rather than fetch order.
  const pendingInDisplayOrder = pendingRegions.flatMap(r => pendingByRegion[r]);

  const shellProps = {
    // "Auditor Dashboard" is now the page of cards that leads here.
    title: 'Reports Approval',
    subtitle: 'Review individual claims and monthly expense reports',
  };

  if (loading && activeTab === 'reports') {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading auditor dashboard...</p>
        </div>
      </PageShell>
    );
  }

  // The server refused: this person has no auditor access, or their cover as
  // an auditor's stand-in has lapsed. Said plainly. The page used to render in
  // full anyway - header, tabs, "Delegate Review" - with an empty table, which
  // read as a working dashboard with nothing in it.
  if (forbidden) {
    return (
      <PageShell {...shellProps}>
        <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No auditor access</h2>
          <p className="text-gray-600">
            This dashboard is for the auditor, and for anyone covering for them while
            their delegation is active. You don&apos;t hold either at the moment - if you
            were covering, that cover may have ended.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-5 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red"
          >
            Back to home
          </button>
        </div>
      </PageShell>
    );
  }

  const safeData = Array.isArray(dashboardData) ? dashboardData : [];

  return (
    <>
    <PageShell {...shellProps}>

        {/* Neither PTAE management nor Delegate Review is here: both are
            cards on the auditor's dashboard, beside the Reports Approval card
            that opens this page (RoleToolCards). The chart of accounts is a
            body of work in its own right, and handing review to someone else
            is something you decide before you start reviewing - not a control
            to meet halfway down the board you are already working in. */}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center justify-between">
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </span>
            <button onClick={refreshData} className="px-4 py-2 bg-red-100 hover:bg-red-200 rounded-xl text-sm font-medium transition-colors">
              Retry
            </button>
          </div>
        )}

        {/* Tab switcher */}
        <div className="grid grid-cols-3 sm:flex gap-1 bg-white rounded-xl shadow-lg p-1 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab('reports')}
            className={`min-w-0 flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold leading-tight text-center transition-colors ${
              activeTab === 'reports'
                ? 'bg-stanford-red text-white shadow'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Monthly Summary Reports
            {safeData.filter(r => r.status === 'submitted').length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-white/30 rounded-full text-xs">
                {safeData.filter(r => r.status === 'submitted').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('claims')}
            className={`min-w-0 flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold leading-tight text-center transition-colors ${
              activeTab === 'claims'
                ? 'bg-stanford-red text-white shadow'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Claims Pre-Approval
            {pendingAuditClaims.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'claims' ? 'bg-white/30' : 'bg-amber-100 text-amber-800'}`}>
                {pendingAuditClaims.length}
              </span>
            )}
          </button>
          {/* Contractors. Their own tab because the auditor is the LAST approver
              here - approving finishes the claim outright, where on the
              pre-approval tab the directors still have to sign. */}
          <button
            onClick={() => setActiveTab('contractors')}
            className={`min-w-0 flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold leading-tight text-center transition-colors ${
              activeTab === 'contractors'
                ? 'bg-stanford-red text-white shadow'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Contractors &amp; Coaches
            {contractorClaims.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'contractors' ? 'bg-white/30' : 'bg-amber-100 text-amber-800'}`}>
                {contractorClaims.length}
              </span>
            )}
          </button>
        </div>

        {/* -- TAB: MONTHLY REPORTS --------------------------------------- */}
        {activeTab === 'reports' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
                <svg className="w-5 h-5 text-stanford-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter Reports
              </div>
              {/* Four selects across, actions on their own row below. Sharing one
                  grid column between two buttons squeezed them to ~90px each,
                  which wrapped "Clear filters" and pushed "Rejection report"
                  past the card edge. */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Region</label>
                  <select
                    value={filters?.region || 'all'}
                    onChange={(e) => handleFilterChange('region', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  >
                    <option value="all">All Regions</option>
                    {Array.isArray(regions) && regions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Year</label>
                  <select
                    value={filters?.year || new Date().getFullYear()}
                    onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  >
                    {Array.isArray(years) && years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Month</label>
                  <select
                    value={filters?.month || 'all'}
                    onChange={(e) => handleFilterChange('month', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  >
                    <option value="all">All Months</option>
                    {Array.isArray(months) && months.map(month => (
                      <option key={month} value={month}>{getMonthName(month)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                  <select
                    value={filters?.status || 'all'}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  >
                    <option value="all">All Statuses</option>
                    {buildReportStatusFilterOptions(safeData).map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-3">
                {/* The rejection report's own period, separate from the report
                    filters above: those choose which monthly reports to list,
                    this chooses which audit decisions to count. */}
                <div className="flex flex-wrap items-center gap-2 mr-auto">
                  <span className="text-sm font-medium text-gray-600">Rejection report period:</span>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                    {[
                      { value: 'fiscal', label: 'Fiscal year' },
                      { value: 'calendar', label: 'Calendar year' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setRrBasis(opt.value)}
                        className={`px-3 py-1.5 text-sm transition-colors ${
                          rrBasis === opt.value
                            ? 'bg-stanford-red text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {rrBasis === 'fiscal' ? (
                    <select
                      value={rrFiscalYear}
                      onChange={(e) => setRrFiscalYear(parseInt(e.target.value, 10))}
                      title="Seed's fiscal year runs 1 September to 31 August"
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    >
                      {fiscalYearOptions(6).map(fy => (
                        <option key={fy} value={fy}>{fiscalYearLabel(fy)}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={rrCalendarYear}
                      onChange={(e) => setRrCalendarYear(parseInt(e.target.value, 10))}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    >
                      {Array.isArray(years) && years.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  )}

                  <select
                    value={rrMonth}
                    onChange={(e) => setRrMonth(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  >
                    <option value="all">Whole year</option>
                    {FISCAL_MONTHS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={clearFilters}
                  className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  Clear filters
                </button>
                {/* Rejection report. Counted from the approval hash chain, not
                    from current claim status - a claim returned twice and then
                    approved reads as 'approved' today, so status alone would
                    report a 0% return rate. */}
                <button
                  onClick={downloadRejectionReport}
                  disabled={rejectionReportBusy}
                  title="Approved vs returned claims, with return rates by region"
                  className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0 px-5 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 inline-flex items-center gap-2 whitespace-nowrap"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {rejectionReportBusy ? 'Generating…' : 'Download rejection report'}
                </button>
              </div>
            </div>

            {/* Stats - three equal tiles on one row. Two half-width cards left
                a lot of dead space and buried "awaiting review", which is the
                number an auditor actually opens this page for. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-gray-800 leading-tight">{safeData.length}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total Reports</div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-gray-800 leading-tight">
                    {safeData.filter(r => r.status === 'submitted').length}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Awaiting Review</div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-gray-800 leading-tight truncate">{formatCurrency(getTotalAmount())}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</div>
                </div>
              </div>
            </div>

            {/* Quick status filters - incl. a clear "Sent Back" view */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleFilterChange('status', 'all')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${(!filters?.status || filters.status === 'all') ? 'bg-stanford-red text-white border-stanford-red' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('status', 'submitted')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${filters?.status === 'submitted' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                Pending Review
              </button>
              <button
                onClick={() => handleFilterChange('status', 'rejected')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${filters?.status === 'rejected' ? 'bg-amber-600 text-white border-amber-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                ↩ Sent Back{safeData.filter(r => r.status === 'rejected').length > 0 ? ` (${safeData.filter(r => r.status === 'rejected').length})` : ''}
              </button>
            </div>

            {/* Reports table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full responsive-table">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Region</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Period</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Amount</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Claims</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Prepared By</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Created</th>
                      <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeData.map((item, index) => (
                      <tr
                        key={item._id || `${item.region}-${item.month}-${item.year}-${index}`}
                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-4 font-semibold text-gray-900" data-cell="title">{item.region || 'N/A'}</td>
                        <td className="px-5 py-4 text-gray-700" data-label="Period">
                          {item.month && item.year ? formatPeriod(item.month, item.year) : 'N/A'}
                        </td>
                        <td className="px-5 py-4 font-bold text-gray-900 tabular-nums" data-label="Amount">{formatCurrency(item.totalAmount)}</td>
                        <td className="px-5 py-4" data-label="Claims">
                          <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${
                            getClaimsCount(item) === 0 ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {getClaimsCount(item)} claims
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-700" data-label="Prepared by">
                          {item.preparedBy ? `${item.preparedBy.firstName} ${item.preparedBy.lastName}` : '-'}
                        </td>
                        <td className="px-5 py-4" data-label="Status">
                          {/* A MonthlyReport, so the REPORT vocabulary - and
                              told from the auditor's side, so a report sitting
                              with them reads "Awaiting Your Review". The claim
                              vocabulary was being applied here, which made
                              'submitted' read "Awaiting Admin Review": the
                              right word for a claim, exactly wrong for a report
                              that had already reached the auditor. */}
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getReportStatus(item.status, item, { viewerRole: 'auditor' }).badge}`}>
                            {getReportStatus(item.status, item, { viewerRole: 'auditor' }).label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-600" data-label="Created">{formatDate(item.createdAt)}</td>
                        <td className="px-5 py-4" data-cell="action">
                          <button
                            onClick={() => handleViewReport(item._id)}
                            className="px-4 py-2 bg-stanford-red text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                            disabled={!item._id}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {safeData.length === 0 && !loading && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Reports Found</h3>
                  <p className="text-gray-500 mb-4">No reports match your current filters.</p>
                  <button onClick={refreshData} className="px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Try Refresh
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* -- TAB: CLAIMS PRE-APPROVAL ------------------------------------ */}
        {(activeTab === 'claims' || activeTab === 'contractors') && (
          <div>
            {/* Section header */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">{queueCopy.heading}</h2>
                  <p className="text-sm text-gray-500 mt-1">{queueCopy.blurb}</p>
                </div>
                {/* sm:shrink-0: the buttons are flex-none at sm+, so letting
                    this row shrink beside the long blurb pushed Refresh past
                    the card's right edge. */}
                <div className="flex items-center gap-3 sm:shrink-0">
                  {/* Walk every pending claim beside its receipts, instead of
                      opening each one and coming back. */}
                  {auditQueue.length > 0 && (
                    <button
                      onClick={() => setShowMassAudit(true)}
                      className="flex-1 sm:flex-none justify-center min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                      <span aria-hidden="true">⇔</span>
                      Mass Audit
                      <span className="text-xs font-normal opacity-80">
                        ({auditQueue.length})
                      </span>
                    </button>
                  )}
                  <button
                    onClick={refreshData}
                    className="flex-1 sm:flex-none justify-center min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {isContractorTab && (
              <div role="tablist" aria-label="Which contractor and coach claims" className="flex flex-wrap gap-2 mb-4">
                {[
                  ['all', 'All'],
                  ['waiting', `Waiting on you (${contractorClaims.length})`],
                  ['approved', `Approved (${contractorHistory.approved.length})`],
                  ['sentBack', `Sent back (${contractorHistory.sentBack.length})`],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={contractorView === key}
                    onClick={() => setContractorView(key)}
                    className={`min-h-[44px] sm:min-h-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      contractorView === key
                        ? 'bg-stanford-red text-white border-stanford-red'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {isContractorTab && showQueue && (
              <h3 className="font-semibold text-gray-800 mb-2">Waiting on you</h3>
            )}

            {!showQueue ? null : (isContractorTab ? contractorLoading : pendingAuditLoading) ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500">Loading pending claims...</p>
              </div>
            ) : pendingAuditForbidden ? (
              /* The server refused this list. Showing "All Caught Up" here - which
                 is what an empty list produced - told someone with no right to
                 the queue that it was clear. */
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Not part of your access</h3>
                <p className="text-gray-500">
                  Auditing individual claims before they reach a report is done by the auditor.
                </p>
              </div>
            ) : auditQueue.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-16 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{queueCopy.emptyTitle}</h3>
                <p className="text-gray-500">{queueCopy.emptyBody}</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full responsive-table">
                    <thead className="bg-stanford-red text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Region</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Claimant</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">PTAE</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Claim #</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Admin Approved</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Audit</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRegions.map(region => (
                        <React.Fragment key={region}>
                          {/* Region grouping row - keeps the by-region view the
                              card layout gave us, inside the report-style table. */}
                          <tr className="bg-gray-100">
                            <td colSpan={10} data-cell="full" className="px-6 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                              {region}
                              <span className="ml-2 font-normal text-gray-500">
                                ({pendingByRegion[region].length} claim{pendingByRegion[region].length !== 1 ? 's' : ''})
                              </span>
                            </td>
                          </tr>

                          {pendingByRegion[region].map(claim => {
                            const isAuditing = auditingClaimId === claim.claimId;
                            const isExpanded = expandedAuditId === claim.claimId;
                            return (
                              <React.Fragment key={claim.claimId}>
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-3 text-sm text-gray-600 hidden md:table-cell">{region}</td>
                                  <td className="px-6 py-3 text-sm font-medium text-gray-900" data-cell="title">
                                    <span className="flex flex-wrap items-center gap-1.5">
                                      {claim.userName || '-'}
                                      <QuestionBadge
                                        threads={questionsForClaim(claim.claimType, claim.claimId)}
                                        onClick={() => setQuestionClaim(claim)}
                                      />
                                    </span>
                                  </td>
                                  <td className="px-6 py-3" data-label="Type">
                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                      claim.claimType === 'ExpenseClaim'
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {claim.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-sm font-semibold text-gray-800 text-right tabular-nums" data-label="Amount">
                                    {formatCurrency(claim.totalAmount || claim.total)}
                                  </td>
                                  {/* Funding assigned by the admin at approval. */}
                                  <td className="px-6 py-3" data-label="PTAE">
                                    {claim.ptaeSplits && claim.ptaeSplits.length > 0 ? (
                                      <div className="flex flex-col gap-0.5">
                                        {claim.ptaeSplits.map((sp, i) => (
                                          <span key={i} className="text-xs font-mono text-gray-700 whitespace-nowrap">
                                            {sp.ptae} <span className="text-gray-400">({formatCurrency(sp.amount)})</span>
                                          </span>
                                        ))}
                                      </div>
                                    ) : claim.ptae ? (
                                      <span className="text-xs font-mono text-gray-700 whitespace-nowrap">{claim.ptae}</span>
                                    ) : (
                                      <span className="text-xs text-amber-700">Not set</span>
                                    )}
                                  </td>

                                  <td className="px-6 py-3 text-xs font-mono text-gray-500" data-label="Claim #">
                                    #{String(claim.claimId).slice(-8).toUpperCase()}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-600" data-label="Admin approved">{formatDate(claim.processedAt)}</td>

                                  {/* Audit cell - mirrors the monthly report's inline audit control */}
                                  <td className="px-6 py-3" data-cell="action">
                                    <button
                                      onClick={() => setExpandedAuditId(isExpanded ? null : claim.claimId)}
                                      className="px-2 py-1 text-sm md:text-xs border border-amber-300 text-amber-700 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
                                    >
                                      {isExpanded ? 'Cancel' : 'Audit'}
                                    </button>
                                  </td>

                                  <td className="px-6 py-3" data-cell="action">
                                    <button
                                      onClick={() => handleViewClaim(claim.claimId, claim.claimType)}
                                      className="px-2 py-1 text-sm md:text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
                                      title="Open this claim read-only"
                                    >
                                      View
                                    </button>
                                  </td>

                                  <td className="px-6 py-3" data-label="Status">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                      Pre-Approval
                                    </span>
                                  </td>
                                </tr>

                                {/* Expanded audit row: notes + approve/reject */}
                                {isExpanded && (
                                  <tr className="bg-amber-50 border-b border-amber-200">
                                    <td colSpan={10} data-cell="full" className="px-6 py-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Audit Notes <span className="text-gray-400 font-normal">(required to ask for a resubmit)</span>
                                      </label>
                                      <textarea
                                        value={auditComments[claim.claimId] || ''}
                                        onChange={(e) => setAuditComments(prev => ({ ...prev, [claim.claimId]: e.target.value }))}
                                        placeholder="Add any notes about this audit..."
                                        rows={2}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent resize-none mb-3"
                                      />
                                      <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2">
                                        <button
                                          onClick={() => setExpandedAuditId(null)}
                                          className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                          Close
                                        </button>
                                        <button
                                          onClick={() => setQuestionClaim(claim)}
                                          className="px-4 py-2 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
                                          title="Ask the admins a question without sending the claim back"
                                        >
                                          ? Ask a Question
                                        </button>
                                        <button
                                          onClick={() => handleReturnClaim(claim.claimId, claim.claimType)}
                                          disabled={isAuditing}
                                          className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                                          title="Send this claim back to the admin to revise and resubmit"
                                        >
                                          Ask to Resubmit
                                        </button>
                                        <button
                                          onClick={() => handleAuditClaim(claim.claimId, claim.claimType)}
                                          disabled={isAuditing}
                                          className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                        >
                                          {isAuditing ? 'Working...' : 'Confirm Audit Approval'}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isContractorTab && contractorView !== 'waiting' && (
              <div className={showQueue ? 'mt-6' : undefined}>
                <ContractorClaimHistory
                  history={contractorHistory}
                  loading={contractorHistoryLoading}
                  show={contractorView === 'all' ? 'all' : contractorView}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  onView={handleViewClaim}
                />
              </div>
            )}
          </div>
        )}

    </PageShell>

      {/* Guided walkthrough over the pending-audit list. No report context here,
          so the problem path is ask-to-resubmit rather than flagging, and there
          is no report-level approval to offer at the end. A sibling of the
          PageShell rather than a child, so it layers against the fixed navbar
          on its own z-index. */}
      {showMassAudit && (
        <MassReviewOverlay
          title={queueCopy.massTitle}
          subtitle={queueCopy.massSubtitle}
          claims={pendingInDisplayOrder}
          role="auditor"
          loadBundle={(claim) =>
            monthlyReportService.getPendingClaimReviewBundle(claim.claimId, claim.claimType)}
          onClose={() => { setShowMassAudit(false); refreshData(); }}
          onMark={(claimId, claimType, options) => auditClaim(claimId, claimType, '', options)}
          onReturn={(claimId, claimType, reason) => returnClaim(claimId, claimType, reason)}
        />
      )}

      {/* One queued claim's clarification questions, from its badge or the
          audit row's Ask button. */}
      {questionClaim && (
        <ClarificationDrawer
          title="Questions about this claim"
          subtitle={`${questionClaim.userName || 'Claim'} · ${questionClaim.claimType === 'PerDiemClaim' ? 'Per Diem' : 'Expense'}`}
          claimType={questionClaim.claimType}
          claimId={questionClaim.claimId}
          onClose={() => setQuestionClaim(null)}
        />
      )}
    </>
  );
};

export default AuditorDashboard;
